import { useCallback, useEffect, useRef, useState } from 'react';

import type { AutocompleteOption } from '../../../types';

import { DEFAULT_OPTION } from '../utils/autocompleteDefault';

type AutocompleteSelectionProps = {
    options: AutocompleteOption[];
    showDefaultOption: boolean;
    onSelectOption: (opt: AutocompleteOption) => void;
    initial?: number;
};

export const useAutocompleteSelection = ({ options, showDefaultOption, onSelectOption, initial = -1 }: AutocompleteSelectionProps) => {
    const [selected, setSelected] = useState<number>(initial);
    const selectedRef = useRef(selected);

    useEffect(() => {
        selectedRef.current = selected;
    }, [selected]);

    const resetSelection = useCallback(() => setSelected(-1), []);

    const selectCurrentOption = useCallback(() => {
        if (selected === 0 && showDefaultOption) {
            onSelectOption(DEFAULT_OPTION);
            return DEFAULT_OPTION;
        }

        const offset = showDefaultOption ? 1 : 0;
        const index = selected - offset;

        if (index >= 0 && index < options.length) {
            const opt = options[index];
            onSelectOption(opt);

            return opt;
        }

        return null;
    }, [selected, showDefaultOption, options, onSelectOption]);

    return {
        selected,
        setSelected,
        selectedRef,
        resetSelection,
        selectCurrentOption,
    };
};
